import type { FilterType } from '@/_containers/data-table/toolbar/use-api-controller';

import { ACCOUNT_RECEIVABLE_LIST_BALANCE_DUE_DEFAULT_CLAUSE } from './apis';

function filterToFilterStr(filters: FilterType[]): string {
  return filters
    .filter((item) => item.value.length > 0)
    .map((item) => `filter[${item.name}]=${item.value.join(',')}`)
    .join('&');
}

/** Mirrors filter_str parsing in useApiController (URL → filter array). */
export function parseFilterStrFromSearch(filter_str: string | undefined): FilterType[] {
  if (!filter_str) {
    return [];
  }
  const nFilters: FilterType[] = [];
  const filtersArray = filter_str.split('&');
  for (const filter of filtersArray) {
    const [name, ...valueParts] = filter.split('=');
    const valueJoined = valueParts.join('=');
    if (!name || valueJoined === undefined) {
      continue;
    }
    nFilters.push({
      name: name.replace('filter[', '').replace(']', ''),
      value: valueJoined.split(','),
    });
  }
  return nFilters;
}

/** Same shape as createAPIParamStr in use-api-controller — used so route loader prefetch matches list page requests. */
export function buildAccountReceivablesApiParamStr(search: {
  per_page?: number;
  page?: number;
  sort?: string;
  search_str?: string;
  filter_str?: string;
}): string {
  const perPage = search.per_page ?? 500;
  const page = search.page ?? 1;
  const sortStr = search.sort ?? '';
  const searchStr = search.search_str ?? '';
  const filters = parseFilterStrFromSearch(search.filter_str);
  const nFilterStr = filterToFilterStr(filters);
  let str = `?per_page=${perPage}&page=${page}`;
  if (nFilterStr) {
    str += `&${nFilterStr}`;
  }
  if (sortStr) {
    str += `&sort=${sortStr}`;
  }
  if (searchStr) {
    str += `&search=${encodeURIComponent(searchStr)}`;
  }
  return str;
}

/**
 * Same merge as useAdvanceFilters.mergeFilters + optional “open balances only” clause.
 * When `forByClientsApi` is set: `/by-clients` and `/all-ar-details` export get `no_balance_owed=true|false` (No Balance Owed) and no default `above[balance_due]=0`.
 * Otherwise (nested `/account-receivables`): when `includeAllReceivables` is false, append `above[balance_due]=0`; when true, omit it.
 */
export function mergeAccountReceivablesListQueryParams(
  apiParamStr: string,
  options: {
    includeAllReceivables: boolean;
    advanceFilterClauses: string[];
    /** Use `no_balance_owed` on `GET .../by-clients` instead of the balance advance clause */
    forByClientsApi?: boolean;
  }
): string {
  const forByClients = options.forByClientsApi ?? false;
  const clauses = [...options.advanceFilterClauses];
  if (!forByClients && !options.includeAllReceivables) {
    clauses.push(ACCOUNT_RECEIVABLE_LIST_BALANCE_DUE_DEFAULT_CLAUSE);
  }
  const advFilterStr = clauses.length > 0 ? `filter[advance_filtering]=${clauses.join(';')}` : '';
  const parts = [apiParamStr, advFilterStr];
  if (forByClients) {
    parts.push(`no_balance_owed=${options.includeAllReceivables ? 'true' : 'false'}`);
  }
  return parts.filter((part) => part.length > 0).join('&');
}
